import { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers";
import {
  BaseQueryFn,
  createApi,
  QueryDefinition,
} from "@reduxjs/toolkit/query/react";
import { IProjectsAPI } from "../../../api/projects.service";
import { ApiService } from "../../../api/api.service";
import { IStandardError } from "../../../shared/types/error.types";
import { convertError } from "../../../shared/utils/errorHandling.utils";

export interface ApiSliceQueryArg<TResult> {
  fn: (x: IProjectsAPI) => MaybePromise<TResult>;
}

function ApiServiceBaseQuery(): BaseQueryFn<
  ApiSliceQueryArg<unknown>,
  unknown,
  IStandardError
> {
  return async (arg: ApiSliceQueryArg<unknown>) => {
    try {
      const res = await arg.fn(ApiService.getInstance().projectsApi);
      return { data: res };
    } catch (err: unknown) {
      if (err) {
        if (typeof err === "object") {
          return { error: convertError(err) };
        }
      }

      return { error: undefined };
    }
  };
}

/*
 * Represents a pointer to a member function on an object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionPointer<TClass, TFunc extends (...args: any) => any> = {
  (obj: TClass): TFunc;
};

/*
 * Gets the inner type in a Promise
 */
type Awaited<T> = T extends PromiseLike<infer U> ? U : T;

/*
 * Replaces a (T | undefined) type with (T | void)
 */
type ReplaceUndefinedWithVoidInUnion<T> = T extends undefined
  ? NonNullable<T> | void
  : T;

/*
 * Gets the (sole) parameter type from a function
 * (multi-arg functions aren't valid for RTK Query)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type SingleParameter<TFunc> = TFunc extends (x: infer R) => infer Y ? R : void;

export const ApiSlice = createApi({
  reducerPath: "cssoApi",
  baseQuery: ApiServiceBaseQuery(),
  endpoints: (builder) => {
    // eslint-disable-next-line @typescript-eslint/ban-types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const helper = <TFunc extends (...args: any) => any>(
      ptr: FunctionPointer<IProjectsAPI, TFunc>
    ): QueryDefinition<
      ReplaceUndefinedWithVoidInUnion<SingleParameter<TFunc>>,
      ReturnType<typeof ApiServiceBaseQuery>,
      never,
      Awaited<ReturnType<TFunc>>,
      string
    > => {
      return builder.query<
        Awaited<ReturnType<TFunc>>,
        ReplaceUndefinedWithVoidInUnion<SingleParameter<TFunc>>
      >({
        query: (
          args: ReplaceUndefinedWithVoidInUnion<SingleParameter<TFunc>>
        ) => ({
          fn: (x: IProjectsAPI): ReturnType<TFunc> => ptr(x)(args),
        }),
      });
    };

    return {
      getProjects: helper((x) => x.getProjects),
      getProjectDetails: helper((x) => x.getProjectDetails),
      getProjectPackages: helper((x) => x.getProjectPackages),
      getPackageDetails: helper((x) => x.getPackageDetails),
      getPackageNotifications: helper((x) => x.getPackageNotifications),
    };
  },
});
